package com.heelion.farmafreelance.models

import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable

/**
 * "Avvisami quando…": una ricerca salvata dall'utente. Quando qualcuno pubblica un turno che
 * corrisponde ([corrisponde]), il server gli manda una notifica (anche push).
 * Un farmacista (o infermiere) cerca turni offerti dalle farmacie; una farmacia cerca disponibilità
 * dei farmacisti o degli infermieri ([professione]).
 */
@Serializable
data class RicercaSalvata(
    val id: String? = null,
    /** Chi riceve l'avviso, es. "FARMACISTA_12" (stesso formato delle notifiche). */
    val utenteId: String,
    /** Ruolo di chi ha salvato la ricerca: FARMACISTA, INFERMIERE o FARMACIA. */
    val ruolo: String,
    /** Sigla della provincia (come salvata nel DB, es. "RM"); null = ovunque. */
    val provincia: String? = null,
    val comune: String? = null,
    /** Giorni della settimana ISO (1 = lunedì ... 7 = domenica); vuoto = tutti. */
    val giorniSettimana: List<Int> = emptyList(),
    /** Per il farmacista: compenso orario minimo accettabile. */
    val compensoMin: Double? = null,
    /** Per la farmacia: compenso orario massimo. */
    val compensoMax: Double? = null,
    /** Per la farmacia: competenza richiesta al professionista (es. "Galenica", "Medicazioni"). */
    val competenza: String? = null,
    /** Per la farmacia: chi cerca. null = farmacista (avvisi salvati prima che ci fossero gli infermieri). */
    val professione: Professione? = null,
    /** Descrizione leggibile, per elenco e notifiche ("turni di farmacie in RM, il sabato, da 30 €/h"). */
    val descrizione: String = ""
)

/** Dati di chi ha pubblicato l'annuncio, necessari al confronto (zona e competenze). */
data class DatiCreatore(
    val provincia: String?,
    val comune: String?,
    /** Altre province in cui opera (farmacista), sigle. */
    val altreProvince: List<String> = emptyList(),
    val competenze: List<String> = emptyList()
)

/**
 * true se l'annuncio pubblicato interessa a chi ha salvato [this]: tipo giusto (un farmacista
 * vuole offerte delle farmacie, una farmacia disponibilità della professione cercata), zona, giorno della
 * settimana, compenso e competenza. Chi pubblica non riceve avvisi dei propri annunci (vedi server).
 */
fun RicercaSalvata.corrisponde(annuncio: AnnuncioTurno, creatore: DatiCreatore): Boolean {
    val tipoGiusto = when (ruolo) {
        // Il professionista vuole i turni delle farmacie per la sua professione.
        "FARMACISTA", "INFERMIERE" -> annuncio.creatoDa == CreatoreTipo.FARMACIA && annuncio.tipo == AnnuncioTipo.OFFRO_LAVORO &&
            annuncio.professioneRichiesta.name == ruolo
        "FARMACIA" -> annuncio.tipo == AnnuncioTipo.CERCO_LAVORO &&
            annuncio.creatoDa.name == (professione ?: Professione.FARMACISTA).name
        else -> false
    }
    if (!tipoGiusto || annuncio.stato != AnnuncioStato.APERTO) return false

    provincia?.let { p ->
        val province = listOfNotNull(creatore.provincia) + creatore.altreProvince
        if (province.none { it.equals(p, ignoreCase = true) }) return false
    }
    comune?.let { c -> if (!c.equals(creatore.comune, ignoreCase = true)) return false }

    if (giorniSettimana.isNotEmpty()) {
        // Il giorno del turno è la data UTC di dataTurno, come nel resto dell'app.
        val giorno = annuncio.dataTurno.toLocalDateTime(TimeZone.UTC).date.dayOfWeek.isoDayNumber
        if (giorno !in giorniSettimana) return false
    }
    compensoMin?.let { min -> if ((annuncio.compenso ?: 0.0) < min) return false }
    compensoMax?.let { max -> if ((annuncio.compenso ?: Double.MAX_VALUE) > max) return false }
    competenza?.let { c -> if (creatore.competenze.none { it.trim().equals(c, ignoreCase = true) }) return false }
    return true
}
